import {
  WhmAlert,
  WhmBox,
  WhmButton,
  WhmCheckbox,
  WhmDialog,
  WhmDialogTitle,
  WhmDialogContent,
  WhmDialogActions,
  WhmFormControlLabel,
  WhmTypography,
  WhmIconButton,
  useMediaQuery,
  whmTheme,
} from "@securitize/reactjs-whm";
import CloseIcon from "@mui/icons-material/Close";
import { Translate, TranslateHtml } from "../../Translation/Translate";
import translationKeys from "../../../contexts/I18nContext/translationKeys";
import { useContext, useState } from "react";
import { CustomWhmLoadingButton } from "../CustomWhmLoadingButton";
import ErrorSnackbar from "../ErrorSnackbar";
import { SnackbarContext } from "../Snackbar/SnackbarContext";
import { useSubmitVerification } from "../../../data/useProfile";

export interface EntityOnboardingSubmitConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * True when there's a KYB-level "Comment for Investor" (legacy `comments`
   * or the newer `additionalComments`) that asks the investor to send extra
   * information by email. Shows an info box plus a required confirmation
   * checkbox that gates the Submit button.
   */
  hasAdditionalInfoComment?: boolean;
}

export default function EntityOnboardingSubmitConfirmationModal({
  open,
  onClose,
  hasAdditionalInfoComment = false,
}: EntityOnboardingSubmitConfirmationModalProps) {
  const isTabletOrMobile = useMediaQuery(whmTheme.breakpoints.down("lg"));
  const { showSnackbar } = useContext(SnackbarContext);
  const submitVerification = useSubmitVerification();
  const [hasEmailedAdditionalInfo, setHasEmailedAdditionalInfo] =
    useState(false);

  const requestSubmit = () =>
    submitVerification.mutate(void 0, {
      onSuccess: () => {
        window.location.reload();
      },
      onError: (error) => {
        showSnackbar({
          severity: "error",
          message: <ErrorSnackbar error={error} />,
        });
        onClose();
      },
    });

  const handleClose = () => {
    setHasEmailedAdditionalInfo(false);
    onClose();
  };

  const isSubmitDisabled =
    hasAdditionalInfoComment && !hasEmailedAdditionalInfo;

  return (
    <WhmDialog
      open={open}
      onClose={handleClose}
      maxWidth={isTabletOrMobile ? false : "xs"}
      fullWidth
      fullScreen={isTabletOrMobile}
    >
      <WhmDialogTitle
        variant="h4"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <Translate
          translationKey={
            translationKeys.ONBOARDING_CARD_CONFIRMATION_MODAL_TITLE
          }
        />
        <WhmIconButton
          aria-label="close"
          onClick={handleClose}
          size="small"
          sx={{
            padding: "4px",
            borderRadius: "100%",
          }}
        >
          <CloseIcon sx={{ fontSize: "20px" }} />
        </WhmIconButton>
      </WhmDialogTitle>

      <WhmDialogContent>
        <WhmBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <WhmTypography variant="body1">
            <TranslateHtml
              translationKey={
                translationKeys.ONBOARDING_CARD_CONFIRMATION_MODAL_DESCRIPTION
              }
            />
          </WhmTypography>

          {hasAdditionalInfoComment && (
            <WhmBox sx={{ width: "100%" }}>
              <WhmAlert severity="info" sx={{ marginBottom: "12px" }}>
                <WhmTypography variant="body2">
                  <TranslateHtml
                    translationKey={
                      translationKeys.ONBOARDING_CARD_CONFIRMATION_MODAL_WARNING_1
                    }
                  />{" "}
                  <TranslateHtml
                    translationKey={
                      translationKeys.ONBOARDING_CARD_CONFIRMATION_MODAL_WARNING_2
                    }
                  />
                </WhmTypography>
              </WhmAlert>
              <WhmFormControlLabel
                control={
                  <WhmCheckbox
                    checked={hasEmailedAdditionalInfo}
                    onChange={(_, checked) =>
                      setHasEmailedAdditionalInfo(checked)
                    }
                  />
                }
                label={
                  <WhmTypography variant="body2">
                    <Translate
                      translationKey={
                        translationKeys.ONBOARDING_CARD_CONFIRMATION_MODAL_EMAILED_ADDITIONAL_INFO_CHECKBOX
                      }
                    />
                  </WhmTypography>
                }
              />
            </WhmBox>
          )}
        </WhmBox>
      </WhmDialogContent>

      <WhmDialogActions>
        <WhmButton variant="outlined" color="secondary" onClick={handleClose}>
          <Translate
            translationKey={
              translationKeys.ONBOARDING_CARD_CONFIRMATION_MODAL_CANCEL_BUTTON
            }
          />
        </WhmButton>
        <CustomWhmLoadingButton
          variant="contained"
          color="primary"
          onClick={requestSubmit}
          disabled={isSubmitDisabled}
          loading={submitVerification.isPending}
        >
          {submitVerification.isPending ? (
            <Translate
              translationKey={
                translationKeys.ONBOARDING_CARD_CONFIRMATION_MODAL_SUBMIT_BUTTON_LOADING
              }
            />
          ) : (
            <Translate
              translationKey={
                translationKeys.ONBOARDING_CARD_CONFIRMATION_MODAL_SUBMIT_BUTTON
              }
            />
          )}
        </CustomWhmLoadingButton>
      </WhmDialogActions>
    </WhmDialog>
  );
}
